import {
  evaluateRynReplaySessionRotation,
  IRynReplayEnvelope,
  IRynReplayIdentity,
  IRynReplayOfflineStore,
  IRynReplayRecorderBackend,
  IRynReplaySessionLifecycleConfig,
  IRynReplayStartOptions,
  IRynReplayTransport,
  isPayloadRecordingAllowedForUrl,
  resolveRynReplaySessionLifecycleConfig,
  RynReplayBatcher,
  RynReplayDelivery,
  RynReplayEventProperties,
  RynReplaySessionEndReason,
  scrubRynReplayUrl,
  RYN_REPLAY_APP_EVENT_TYPE,
  RYN_REPLAY_IDENTITY_EVENT_TYPE,
  RYN_REPLAY_SESSION_END_EVENT_TYPE,
  RYN_REPLAY_SESSION_START_EVENT_TYPE,
} from '@ups/xplat/base/ryn-replay';
import { sanitizeRynReplayEvent } from './ryn-replay-rrweb-event-scrub';
import {
  IRynReplayDevtoolsCapture,
  RynReplayDevtoolsCapture,
} from './ryn-replay-devtools-capture';
import {
  IRynReplayViewportCapture,
  RynReplayViewportCapture,
} from './ryn-replay-viewport-capture';
import {
  IRynReplayPerformanceCapture,
  RynReplayPerformanceCapture,
} from './ryn-replay-performance-capture';
import {
  readRynReplayClientContext,
  rynReplayClientContextPayload,
  RYN_REPLAY_CONTEXT_EVENT_TYPE,
} from './ryn-replay-context-capture';
import { buildRrwebRecordOptions } from './ryn-replay-recorder.masking-policy';

/**
 * The rrweb capture backend (#30070 capture slice). This is the implementation
 * the seam in `ryn-replay-recorder.backend.ts` was left for; it fills record()
 * wiring, gzip batching, transport, IndexedDB offline buffering, and
 * console/network (devtools) capture.
 *
 * WHY rrweb IS LOADED LAZILY. `start()` dynamically imports rrweb rather than
 * importing it at module scope. This library is pulled in by core.module in
 * every web app in the monorepo, and a static import would add rrweb to every
 * app's main bundle including the ones that never opted in. The import is fired
 * from `start()`, which only runs for an app whose `rynReplay.enabled` is true,
 * so a non-recording app pays nothing.
 *
 * Consequence, and it is deliberate: capture begins one microtask-plus-network
 * after `start()` returns rather than synchronously. The first events of a page
 * load can therefore be missed. That is the correct trade against shipping
 * rrweb to every app, and rrweb's own FullSnapshot on attach means the replay
 * still opens on a complete DOM rather than a partial one.
 */

export * from './ryn-replay-recorder.rrweb-backend.types';
import {
  IRynReplayRrwebBackendConfig,
  RynReplayRecordFn,
  RYN_REPLAY_EVENT_TYPE_NAMES,
  RYN_REPLAY_UNKNOWN_EVENT_TYPE,
} from './ryn-replay-recorder.rrweb-backend.types';

const DEFAULT_FLUSH_INTERVAL_MS = 5_000;

export class RrwebRynReplayRecorderBackend implements IRynReplayRecorderBackend {
  private batcher?: RynReplayBatcher;
  private stopRecording?: () => void;
  private flushTimer?: ReturnType<typeof setInterval>;
  private sessionId?: string;
  private startOptions?: IRynReplayStartOptions;
  /** Stamped on every envelope once recording starts (#30545). */
  private appVersion?: string;
  private buildNumber?: string;
  /** Stamped on every envelope once recording starts (#30211). */
  private userAgent?: string;
  private environmentName?: string;
  private paused = false;
  private identity?: IRynReplayIdentity;
  private unloadHandler?: () => void;
  private warnedMissingSecurityUserId = false;

  /**
   * `visibilitychange` listener, the PRIMARY terminal flush. Held separately
   * from `unloadHandler` so both can be removed independently.
   */
  private visibilityHandler?: () => void;
  private onlineHandler?: () => void;
  private devtools?: IRynReplayDevtoolsCapture;
  private viewport?: IRynReplayViewportCapture;
  private performance?: IRynReplayPerformanceCapture;
  private pendingContextPayload?: Record<string, unknown>;
  private sessionStartedMs = 0;
  private lastActivityMs = 0;
  private readonly lifecycleConfig: IRynReplaySessionLifecycleConfig;
  private readonly delivery: RynReplayDelivery;
  private rotating = false;
  /** Set when idle ends a segment; capture resumes on the next user activity. */
  private idleSegmentPending?: { previousSessionId: string };
  private idleResumeHandler?: () => void;

  constructor(
    private readonly transport: IRynReplayTransport,
    private readonly config: IRynReplayRrwebBackendConfig,
    /** Injected for tests. Production resolves rrweb's `record` lazily. */
    private readonly recordFn?: RynReplayRecordFn,
    private readonly offlineStore?: IRynReplayOfflineStore
  ) {
    this.lifecycleConfig = resolveRynReplaySessionLifecycleConfig(
      config.sessionLifecycle
    );
    this.delivery = new RynReplayDelivery(transport, offlineStore);
  }

  start(options: IRynReplayStartOptions): void {
    if (this.stopRecording || this.startOptions) {
      // Already recording. Re-entry would attach a second rrweb listener set
      // and double every event in the session.
      return;
    }

    this.sessionId = options.sessionId;
    this.startOptions = options;
    this.appVersion = options.appVersion;
    this.buildNumber = options.buildNumber;
    this.environmentName = options.environment;
    this.batcher = new RynReplayBatcher(this.config.limits);
    this.paused = false;
    const now = Date.now();
    this.sessionStartedMs = now;
    this.lastActivityMs = now;

    this.attachRecorder(options);
    this.attachDevtools(options);
    this.captureClientContext();
    this.attachViewport();
    this.attachPerformance();
    this.emitClientContext();
    this.startFlushTimer();
    this.attachUnloadFlush();
    this.attachOnlineDrain();
    void this.drainOffline();
  }

  stop(): void {
    this.stopRecording?.();
    this.stopRecording = undefined;
    this.detachDevtools();
    this.detachViewport();
    this.detachPerformance();
    this.detachIdleResumeListeners();
    this.stopFlushTimer();
    this.detachUnloadFlush();
    this.detachOnlineDrain();
    // Final flush: without it the tail of every session is lost.
    //
    // Ordering is load-bearing. `flush()` drains the batcher SYNCHRONOUSLY,
    // before its first await, so clearing the batcher on the next line cannot
    // race the drain. Reversing these two lines silently discards the tail.
    void this.flush();
    this.startOptions = undefined;
    this.appVersion = undefined;
    this.buildNumber = undefined;
    this.userAgent = undefined;
    this.environmentName = undefined;
    this.pendingContextPayload = undefined;
    // Clearing these two is what actually ENDS the session. `enqueue` guards on
    // sessionId and batcher only, so leaving them set means a later track() or
    // setIdentity() (the help desk modal, a Halo hand-off) keeps buffering onto
    // a session the caller believes is stopped, and still POSTs it the moment a
    // size cap trips. Anything under the cap is buffered and then dropped with
    // no timer and no unload handler left to send it.
    this.sessionId = undefined;
    this.batcher = undefined;
  }

  /**
   * rrweb has no native pause, so this detaches the listeners and `resume()`
   * re-attaches them. The consequence is visible in playback and is the honest
   * one: the gap appears as a jump, and re-attaching emits a fresh FullSnapshot
   * so the replay resumes from a known-complete DOM rather than replaying
   * deltas against a DOM that changed while nobody was looking.
   */
  pause(): void {
    if (this.paused) {
      return;
    }
    this.paused = true;
    this.detachIdleResumeListeners();
    this.stopRecording?.();
    this.stopRecording = undefined;
    this.detachDevtools();
    this.detachViewport();
    this.detachPerformance();
    void this.flush();
  }

  resume(): void {
    if (!this.paused || !this.startOptions) {
      return;
    }
    if (this.idleSegmentPending) {
      this.beginIdleSegmentCapture();
      return;
    }
    this.paused = false;
    this.attachRecorder(this.startOptions);
    this.attachDevtools(this.startOptions);
    this.attachViewport();
    this.attachPerformance();
  }

  addEvent(name: string, properties?: RynReplayEventProperties): void {
    this.enqueue(RYN_REPLAY_APP_EVENT_TYPE, {
      name,
      properties: properties ?? {},
    });
  }

  setIdentity(identity: IRynReplayIdentity): void {
    this.identity = identity;

    // Somebody logged in and we still have no security user id, so this
    // session's row will have an empty user on it. Say so once.
    //
    // The reason this warning exists at all: a NULL user column looks exactly
    // the same whether nobody was signed in or the wiring is broken. Those two
    // have to be distinguishable from outside or the broken one hides behind the
    // normal one indefinitely. That is precisely how the replay read surface
    // shipped with no client at all, so the guard goes in with the feature.
    if (!identity.securityUserId && !this.warnedMissingSecurityUserId) {
      this.warnedMissingSecurityUserId = true;
      console.warn(
        'Ryn Replay: a user was identified but carried no securityUserId, so this ' +
          'session will be stored with no user against it. The app is passing an ' +
          'identity without IEmployeeInfo.UserId. This is a wiring bug, not a ' +
          'signed-out session.'
      );
    }

    this.enqueue(RYN_REPLAY_IDENTITY_EVENT_TYPE, {
      ...identity,
    } as unknown as Record<string, unknown>);
  }

  clearIdentity(): void {
    this.identity = undefined;
    // Top-level `anonymized` is what clears the SQL index row (ingest projection
    // never opens Payload). Payload keeps the same flag for stream/devtools
    // readers that already look at Identity event bodies.
    this.enqueue(
      RYN_REPLAY_IDENTITY_EVENT_TYPE,
      { anonymized: true },
      undefined,
      { anonymized: true }
    );
  }

  /**
   * Send everything buffered, plus any batch that previously failed.
   *
   * Pass `unloading` only from the page-hide path. It turns on `keepalive`, so
   * the request can outlive the document. Everywhere else it must stay off:
   * the browser caps keepalive body size and rejects the whole request past it,
   * which silently ate every full snapshot until this was split.
   */
  async flush(unloading = false): Promise<void> {
    const pending = this.delivery.takePending();

    const buffered = this.batcher?.drain() ?? [];
    if (buffered.length > 0) {
      pending.push(buffered);
    }

    if (unloading) {
      // Concurrently, NOT in series. On a real close the first dispatch may
      // never settle, and an awaited loop would then never issue batches 2..n
      // at all. With a non-empty retry queue that loses the whole session
      // instead of one batch, and the loss is silent because the document dies
      // holding the queue.
      await Promise.all(
        pending.map((batch) =>
          this.delivery.send(batch, {
            sessionId: this.sessionId,
            unloading: true,
          })
        )
      );
      return;
    }

    for (const batch of pending) {
      await this.delivery.send(batch, {
        sessionId: this.sessionId,
        unloading,
      });
    }
  }

  /**
   * Drain oldest persisted batches once the network is back.
   *
   * Enqueue ordering, the stop-at-first-failure rule and the re-entrancy guard
   * all live in the shared delivery queue, so the NativeScript recorder cannot
   * drift from this — it was a verbatim copy before the queue existed.
   */
  async drainOffline(): Promise<void> {
    await this.delivery.drainOffline();
  }

  /**
   * End the current session segment. Idle walk-away pauses capture until the
   * user returns; max-duration keeps recording on a fresh session id (#31331).
   */
  async rotateSession(
    newSessionId: string,
    reason: RynReplaySessionEndReason
  ): Promise<void> {
    const options = this.startOptions;
    if (!options || !this.sessionId || this.paused) {
      return;
    }

    const previousSessionId = this.sessionId;

    this.enqueue(RYN_REPLAY_SESSION_END_EVENT_TYPE, {
      reason,
      nextSessionId: newSessionId,
    });
    await this.flush();

    this.stopRecording?.();
    this.stopRecording = undefined;
    this.detachDevtools();
    this.detachViewport();
    this.detachPerformance();

    this.sessionId = newSessionId;
    this.batcher = new RynReplayBatcher(this.config.limits);
    this.pendingContextPayload = undefined;

    if (reason === 'idle') {
      this.paused = true;
      this.idleSegmentPending = { previousSessionId };
      this.attachIdleResumeListeners();
      return;
    }

    const now = Date.now();
    this.sessionStartedMs = now;
    this.lastActivityMs = now;
    this.captureClientContext();

    this.attachRecorder(options);
    this.attachDevtools(options);
    this.attachViewport();
    this.attachPerformance();
    this.enqueue(RYN_REPLAY_SESSION_START_EVENT_TYPE, {
      reason,
      previousSessionId,
    });
    this.emitClientContext();

    if (this.identity) {
      this.enqueue(RYN_REPLAY_IDENTITY_EVENT_TYPE, {
        ...this.identity,
      } as unknown as Record<string, unknown>);
    }
  }

  private attachDevtools(options: IRynReplayStartOptions): void {
    this.detachDevtools();
    this.devtools = new RynReplayDevtoolsCapture({
      emit: (type, payload) => this.enqueue(type, payload),
      isPayloadRecordingAllowed: (url) =>
        isPayloadRecordingAllowedForUrl(options.policy, url),
      ingestUrl: this.config.ingestUrl,
      correlationAllowedApiBaseUrls: this.config.correlationAllowedApiBaseUrls,
      // PR 3639 review: an app whose policy opts into maskAllText also wants
      // console.error redacted like log/info/warn, not the full diagnostic tree.
      consoleErrorMode: options.policy.maskAllText ? 'privacy' : 'diagnostic',
    });
    this.devtools.install();
  }

  private detachDevtools(): void {
    this.devtools?.uninstall();
    this.devtools = undefined;
  }

  private attachViewport(): void {
    this.detachViewport();
    this.viewport = new RynReplayViewportCapture((type, payload) =>
      this.enqueue(type, payload)
    );
    this.viewport.install();
  }

  private detachViewport(): void {
    this.viewport?.uninstall();
    this.viewport = undefined;
  }

  private attachPerformance(): void {
    this.detachPerformance();
    this.performance = new RynReplayPerformanceCapture((type, payload) =>
      this.enqueue(type, payload)
    );
    this.performance.install();
  }

  private detachPerformance(): void {
    this.performance?.uninstall();
    this.performance = undefined;
  }

  /**
   * Read UA / env once at start (#30211) so every subsequent envelope (including
   * Viewport) can stamp the indexable fields before the Context event itself
   * is written.
   */
  private captureClientContext(): void {
    const snapshot = readRynReplayClientContext({
      appVersion: this.appVersion,
      buildNumber: this.buildNumber,
      environment: this.environmentName,
    });
    this.userAgent = snapshot.userAgent || undefined;
    this.environmentName = snapshot.environment || this.environmentName;
    this.pendingContextPayload = rynReplayClientContextPayload(snapshot);
  }

  /**
   * One Context envelope at session start (#30211). Payload holds the full
   * snapshot for the player; indexable fields already ride every envelope.
   */
  private emitClientContext(): void {
    if (!this.pendingContextPayload) {
      return;
    }
    const payload = this.pendingContextPayload;
    this.pendingContextPayload = undefined;
    this.enqueue(RYN_REPLAY_CONTEXT_EVENT_TYPE, payload);
  }

  private attachRecorder(options: IRynReplayStartOptions): void {
    const wire = (record: RynReplayRecordFn) => {
      // A stop() or pause() between firing the import and it resolving must not
      // start capture on a session that is no longer recording.
      if (this.paused || !this.startOptions) {
        return;
      }
      this.stopRecording = record({
        emit: (event: unknown) => this.onRrwebEvent(event),
        ...buildRrwebRecordOptions(
          options.policy,
          this.config.recordCanvas ?? false
        ),
      });
    };

    if (this.recordFn) {
      wire(this.recordFn);
      return;
    }

    void import('rrweb')
      .then((rrweb) => wire(rrweb.record as unknown as RynReplayRecordFn))
      .catch(() => {
        // Capture is best-effort. A failed chunk load must never break the app
        // the user is actually trying to use.
      });
  }

  private onRrwebEvent(event: unknown): void {
    // Scrub raw URLs out of the payload before it is buffered. rrweb's Meta
    // event and FullSnapshot carry the page URL verbatim (OAuth code/state on a
    // callback), which the envelope-url scrub in enqueue() does not reach. See
    // sanitizeRynReplayEvent and bugs #31380 / #31385.
    const sanitized = sanitizeRynReplayEvent(event);
    const typed = sanitized as { type?: number; timestamp?: number };
    const typeName =
      (typeof typed?.type === 'number'
        ? RYN_REPLAY_EVENT_TYPE_NAMES[typed.type]
        : undefined) ?? RYN_REPLAY_UNKNOWN_EVENT_TYPE;

    this.enqueue(
      typeName,
      { event: sanitized as Record<string, unknown> },
      typed?.timestamp
    );
  }

  /**
   * Build the envelope and buffer it.
   *
   * `url` is stamped on the ENVELOPE, not inside `payload`. It is the one
   * non-opaque field the server reads, and it is what populates
   * `RynReplaySessionPage` and `RynReplaySession.LandingUrl`. The server collapses
   * runs of the same url into one trail row, so stamping every event costs nothing
   * and means the page trail needs no event-type vocabulary on either side. The
   * rrweb event stays untouched under `payload.event` rather than being spread, so
   * a future rrweb field named `url` cannot collide with ours.
   *
   * ══ THIS MOVED, AND SHIPPING IT THE OLD WAY FAILS SILENTLY ══
   * It used to live at `payload.url`, which matched the server at the time this
   * recorder was written. Slice B then moved the server to read a TOP-LEVEL `url`
   * (`ReplayEventEnvelope.Url`, and `ReplaySessionIndexProjection` reads
   * `envelope.Url` and is documented never to open `Payload`). The recorder was
   * never updated, because nothing had wired it up yet, so no test and no
   * environment ever exercised the pair together.
   *
   * The failure that mismatch produces is the dangerous kind: recordings still
   * store, ingest still answers 202, and only the derived page trail is empty with
   * `LandingUrl` NULL. A smoke test passes. Nothing errors. Which is exactly why
   * the pairing is now pinned by a spec that asserts `url` is on the envelope AND
   * absent from `payload`.
   */
  private enqueue(
    type: string,
    payload: Record<string, unknown>,
    ts?: number,
    envelopeExtras?: Pick<IRynReplayEnvelope, 'anonymized'>
  ): void {
    if (this.paused && this.idleSegmentPending) {
      this.beginIdleSegmentCapture();
    }
    if (!this.sessionId || !this.batcher) {
      return;
    }

    this.lastActivityMs = Date.now();

    const envelope: IRynReplayEnvelope = {
      sessionId: this.sessionId,
      app: this.config.app,
      platform: this.config.platform ?? 'web',
      ts: ts ?? Date.now(),
      type,
      // Scrubbed at CAPTURE time, not at read time. Anything sensitive that
      // reaches the batcher is already on its way to permanent storage and to
      // the LandingUrl column, so redacting later would only hide it from one
      // reader. See scrubRynReplayUrl and bug #30106.
      url: scrubRynReplayUrl(globalThis.location?.href ?? ''),
      // Stamped on every event rather than once per session, because a session
      // has no single moment we could stamp. Events start flowing at boot,
      // before login, and the server takes the first identity it finds in a
      // batch. Sending it every time means whichever batch happens to carry the
      // login boundary is the one that tells the server, with no extra
      // bookkeeping here. Omitted entirely while nobody is signed in, since the
      // server rejects a present-but-empty value. Also omitted on anonymize
      // events — those clear the index rather than fill it.
      ...(this.identity?.securityUserId && !envelopeExtras?.anonymized
        ? { securityUserId: this.identity.securityUserId }
        : {}),
      ...(envelopeExtras?.anonymized ? { anonymized: true } : {}),
      ...(this.appVersion ? { appVersion: this.appVersion } : {}),
      ...(this.buildNumber ? { buildNumber: this.buildNumber } : {}),
      ...(this.userAgent ? { userAgent: this.userAgent } : {}),
      ...(this.environmentName ? { environment: this.environmentName } : {}),
      payload,
    };

    const ready = this.batcher.add(envelope);
    if (ready) {
      void this.delivery.send(ready, { sessionId: this.sessionId });
    }
  }

  private attachOnlineDrain(): void {
    if (
      !this.offlineStore ||
      typeof globalThis.addEventListener !== 'function'
    ) {
      return;
    }
    this.onlineHandler = () => {
      void this.flush().then(() => this.drainOffline());
    };
    globalThis.addEventListener('online', this.onlineHandler);
  }

  private detachOnlineDrain(): void {
    if (
      this.onlineHandler &&
      typeof globalThis.removeEventListener === 'function'
    ) {
      globalThis.removeEventListener('online', this.onlineHandler);
    }
    this.onlineHandler = undefined;
  }

  private startFlushTimer(): void {
    const interval = this.config.flushIntervalMs ?? DEFAULT_FLUSH_INTERVAL_MS;
    this.flushTimer = setInterval(() => void this.onFlushTimerTick(), interval);
  }

  private async onFlushTimerTick(): Promise<void> {
    await this.maybeRotateSession();
    await this.flush();
  }

  private async maybeRotateSession(): Promise<void> {
    if (
      this.rotating ||
      this.paused ||
      !this.startOptions?.onSessionRotation ||
      !this.sessionId
    ) {
      return;
    }

    const reason = evaluateRynReplaySessionRotation({
      nowMs: Date.now(),
      state: {
        sessionStartedMs: this.sessionStartedMs,
        lastActivityMs: this.lastActivityMs,
      },
      config: this.lifecycleConfig,
    });
    if (!reason) {
      return;
    }

    this.rotating = true;
    try {
      const newSessionId = await this.startOptions.onSessionRotation(reason);
      if (!newSessionId || newSessionId === this.sessionId) {
        return;
      }
      await this.rotateSession(newSessionId, reason);
    } finally {
      this.rotating = false;
    }
  }

  /**
   * Resume capture after an idle-ended segment: attach rrweb and stamp
   * SessionStart on the session id reserved at walk-away.
   */
  private beginIdleSegmentCapture(): void {
    const options = this.startOptions;
    const pending = this.idleSegmentPending;
    if (!options || !pending || !this.sessionId || !this.batcher) {
      return;
    }

    this.idleSegmentPending = undefined;
    this.detachIdleResumeListeners();
    this.paused = false;

    const now = Date.now();
    this.sessionStartedMs = now;
    this.lastActivityMs = now;
    this.captureClientContext();

    this.attachRecorder(options);
    this.attachDevtools(options);
    this.attachViewport();
    this.attachPerformance();
    this.enqueue(RYN_REPLAY_SESSION_START_EVENT_TYPE, {
      reason: 'idle',
      previousSessionId: pending.previousSessionId,
    });
    this.emitClientContext();

    if (this.identity) {
      this.enqueue(RYN_REPLAY_IDENTITY_EVENT_TYPE, {
        ...this.identity,
      } as unknown as Record<string, unknown>);
    }
  }

  private attachIdleResumeListeners(): void {
    if (this.idleResumeHandler || typeof document === 'undefined') {
      return;
    }

    const handler = () => {
      this.beginIdleSegmentCapture();
    };
    this.idleResumeHandler = handler;
    document.addEventListener('pointerdown', handler, {
      capture: true,
      passive: true,
    });
    document.addEventListener('keydown', handler, { capture: true });
  }

  private detachIdleResumeListeners(): void {
    const handler = this.idleResumeHandler;
    if (!handler || typeof document === 'undefined') {
      this.idleResumeHandler = undefined;
      return;
    }
    document.removeEventListener('pointerdown', handler, { capture: true });
    document.removeEventListener('keydown', handler, { capture: true });
    this.idleResumeHandler = undefined;
  }

  private stopFlushTimer(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = undefined;
    }
  }

  /**
   * TWO terminal signals, and the order matters.
   *
   * `visibilitychange` to hidden is the PRIMARY one. It fires before `pagehide`
   * on a tab close or a navigation, and critically the document is still ALIVE
   * when it runs, so the async work in the send path completes normally.
   *
   * That async work is the reason `pagehide` alone was not enough. `send`
   * awaits `compress()` before it calls `fetch`, and `gzipRynReplayBatch` goes
   * through `CompressionStream` plus `new Response(stream).arrayBuffer()`, which
   * is stream tasks rather than a single microtask. So on a real close the fetch
   * was not issued DURING the handler, it was scheduled for after it.
   * `keepalive` keeps an in-flight request alive; it does nothing for a request
   * that was never started. The flag was set correctly and the request still
   * never left.
   *
   * `pagehide` stays as the backstop for the cases visibilitychange misses, and
   * `unload` is still deliberately unused: it is unreliable and it blocks the
   * back/forward cache.
   *
   * Flushing on every tab switch is the accepted cost. It is a small extra
   * request on a hidden tab, against the alternative of losing the tail of every
   * session that ends by closing.
   */
  private attachUnloadFlush(): void {
    if (typeof globalThis.addEventListener !== 'function') {
      return;
    }
    this.unloadHandler = () => void this.flush(true);
    this.visibilityHandler = () => {
      const state = (globalThis as { document?: { visibilityState?: string } })
        .document?.visibilityState;
      // Only hidden. 'visible' means the user came back, and flushing there
      // would fire on every tab focus for no benefit.
      if (state === 'hidden') {
        void this.flush(true);
      }
    };
    globalThis.addEventListener('pagehide', this.unloadHandler);
    globalThis.addEventListener('visibilitychange', this.visibilityHandler);
  }

  private detachUnloadFlush(): void {
    const canRemove = typeof globalThis.removeEventListener === 'function';
    if (this.unloadHandler && canRemove) {
      globalThis.removeEventListener('pagehide', this.unloadHandler);
    }
    if (this.visibilityHandler && canRemove) {
      globalThis.removeEventListener(
        'visibilitychange',
        this.visibilityHandler
      );
    }
    this.unloadHandler = undefined;
    this.visibilityHandler = undefined;
  }

  /** Exposed for the suite and for a support engineer debugging a live session. */
  get currentIdentity(): IRynReplayIdentity | undefined {
    return this.identity;
  }
}
